import { useClient } from '@cortex/react-query';
import type { MCPConnection } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { routes } from '../../lib/constants';
import { mcpStatus, type McpStatus } from '../../lib/mcp-auth-status';

/**
 * Shared MCP tool-refs + per-tool-allowlist editor (DEV-16944).
 *
 * Extracted from the agent editor's ToolsSection so the same rich picker
 * (connection add/remove, expandable per-connection rows with grouped
 * per-tool checkboxes, lazy tools/list probing) can back both:
 *   - /agents/$agentId — persists via PATCH v1/agents/:id, and
 *   - the llm-agent node config panel — persists via the workflow editor's
 *     updateNode path.
 *
 * The component is fully controlled and carries NO persistence: every edit
 * flows through `onChange` with the complete next value. Semantics (mirrors
 * packages/node-types/src/llm-agent/resolve-tools.ts):
 *   - `toolRefs` entries are MCP connection NAMES (or platform tool ids).
 *   - `toolAllowlists` is keyed by connection name; an absent (or empty)
 *     entry means "expose every advertised tool".
 */

export interface IMcpToolsValue {
  toolRefs: string[];
  toolAllowlists: Record<string, string[]>;
}

export interface IMcpToolsEditorProps extends IMcpToolsValue {
  onChange: (next: IMcpToolsValue) => void;
  /** Blocks all edits (e.g. while the consumer's save is in flight). */
  disabled?: boolean;
  /**
   * Render refs that don't match any MCP connection name (platform tool ids)
   * as rows with a "Platform" badge — the agent page behavior. The node panel
   * passes `false` because it renders those refs in its own free-text area;
   * unknown refs are still preserved untouched in every `onChange` payload.
   */
  showUnknownRefs?: boolean;
}

// ─── Pure value transitions (exported for tests; the UI is just glue) ───────

/** Attach a connection by name. No-op when already attached. */
export function addConnectionRef(value: IMcpToolsValue, name: string): IMcpToolsValue {
  if (value.toolRefs.includes(name)) return value;
  return { toolRefs: [...value.toolRefs, name], toolAllowlists: value.toolAllowlists };
}

/**
 * Detach a ref. Also drops any allowlist entry for it so the restriction
 * doesn't linger as dead config.
 */
export function removeToolRef(value: IMcpToolsValue, ref: string): IMcpToolsValue {
  const toolRefs = value.toolRefs.filter((r) => r !== ref);
  if (!(ref in value.toolAllowlists)) return { toolRefs, toolAllowlists: value.toolAllowlists };
  const toolAllowlists = { ...value.toolAllowlists };
  delete toolAllowlists[ref];
  return { toolRefs, toolAllowlists };
}

/**
 * Set/clear a connection's allowlist. `undefined` (or empty) removes the
 * entry entirely → back to full access for that server.
 */
export function setConnectionAllowlist(
  value: IMcpToolsValue,
  ref: string,
  next: string[] | undefined,
): IMcpToolsValue {
  const toolAllowlists = { ...value.toolAllowlists };
  if (!next || next.length === 0) delete toolAllowlists[ref];
  else toolAllowlists[ref] = next;
  return { toolRefs: value.toolRefs, toolAllowlists };
}

/**
 * True when `next` grants exactly the advertised surface: every advertised
 * tool selected AND nothing else. Count equality is NOT enough — a persisted
 * allowlist can carry stale names the server no longer advertises, and
 * collapsing on count alone would silently widen the grant to "all tools"
 * (exposing advertised tools the user never checked).
 */
function coversExactlyAdvertised(next: string[], advertised: string[]): boolean {
  if (advertised.length === 0) return false;
  const nextSet = new Set(next);
  return nextSet.size === advertised.length && advertised.every((n) => nextSet.has(n));
}

/**
 * Toggle one tool. Toggling from the unrestricted state (`restrictedTo ===
 * null`) seeds the allowlist with every advertised tool minus the one being
 * switched off — so the first uncheck creates the restriction. Re-checking
 * everything (or unchecking all) collapses back to `undefined` = "allow all";
 * the everything-checked collapse requires the selection to exactly equal the
 * advertised set (stale allowlist entries block it — see
 * `coversExactlyAdvertised`).
 */
export function toggleToolInAllowlist(
  restrictedTo: string[] | null,
  advertised: string[],
  name: string,
): string[] | undefined {
  const base = restrictedTo !== null ? restrictedTo.slice() : advertised.slice();
  const next = base.includes(name) ? base.filter((n) => n !== name) : [...base, name];
  if (next.length === 0 || coversExactlyAdvertised(next, advertised)) {
    return undefined;
  }
  return next;
}

/**
 * Bulk-toggle every tool in a group. `turnOn === true` unions the group's
 * names into the allowlist; `false` removes them. Same collapse rules as
 * `toggleToolInAllowlist`.
 */
export function toggleGroupInAllowlist(
  restrictedTo: string[] | null,
  advertised: string[],
  names: string[],
  turnOn: boolean,
): string[] | undefined {
  const base = restrictedTo !== null ? restrictedTo.slice() : advertised.slice();
  const nextSet = new Set(base);
  for (const n of names) {
    if (turnOn) nextSet.add(n);
    else nextSet.delete(n);
  }
  const next = [...nextSet];
  if (next.length === 0 || coversExactlyAdvertised(next, advertised)) {
    return undefined;
  }
  return next;
}

// ─── Connection status + tool grouping helpers ──────────────────────────────

interface IPageEnvelope<T> {
  items: T[];
  total: number;
}

/** Structural slice of the SDK's ky instance the fetch helper needs. */
interface IHttpGet {
  get: (url: string, opts?: { searchParams?: Record<string, string> }) => { json<T>(): Promise<T> };
}

/** Backstop for pathological orgs — far above any real connection count. */
const mcpConnectionsFetchCap = 1000;

/**
 * Fetch every org+catalog connection by following the pagination envelope —
 * a single fixed `limit=200` page hid rows past 200, misclassifying their
 * refs as platform/unknown. Returns the envelope shape every consumer of the
 * `['mcp-connections']` query key expects.
 */
export async function fetchAllMcpConnections(
  http: IHttpGet,
): Promise<IPageEnvelope<MCPConnection>> {
  const items: MCPConnection[] = [];
  let total = 0;
  let offset = 0;
  for (;;) {
    const page = await http
      .get('v1/mcp-connections', { searchParams: { limit: '200', offset: String(offset) } })
      .json<IPageEnvelope<MCPConnection> & { hasMore?: boolean }>();
    items.push(...page.items);
    total = page.total ?? items.length;
    if (
      page.hasMore !== true ||
      page.items.length === 0 ||
      items.length >= mcpConnectionsFetchCap
    ) {
      return { items: items.slice(0, mcpConnectionsFetchCap), total };
    }
    offset += page.items.length;
  }
}

/**
 * Resolve duplicate connection names the way the runtime resolver does: an
 * org-owned row wins over a catalog (`orgId` null) row of the same name
 * (resolve-tools.ts prefers the org override). Among rows of equal
 * ownership, first-seen wins (the API lists newest first). Also keeps the
 * add-dropdown free of duplicate `<option>` names.
 */
export function dedupeConnectionsByName(rows: MCPConnection[]): MCPConnection[] {
  const byName = new Map<string, MCPConnection>();
  for (const row of rows) {
    const existing = byName.get(row.name);
    if (!existing) {
      byName.set(row.name, row);
      continue;
    }
    const existingIsOrg = existing.orgId !== null && existing.orgId !== undefined;
    const rowIsOrg = row.orgId !== null && row.orgId !== undefined;
    if (rowIsOrg && !existingIsOrg) byName.set(row.name, row);
  }
  return [...byName.values()];
}

// `mcpStatus` / `McpStatus` now live in ../../lib/mcp-auth-status (imported
// above) so the canvas and workflow-editor context can share the exact same
// runtime-callability rule without importing this heavy editor component.

function statusLabel(status: McpStatus): string {
  return status === 'ready' ? '● Connected' : '○ Needs connect';
}

/**
 * Text + tooltip for one connection in a "pick a connection" dropdown.
 *
 * Shared so every such dropdown reads identically — the `llm-agent` picker
 * below and the `mcp-query` node form both call this, rather than each
 * inventing its own "name + is-it-authed" formatting (DEV-17129). The label
 * leads with the config-only status because that's what decides whether the
 * connection is callable at runtime at all; the tooltip falls back to the
 * transport + endpoint when the connection carries no description.
 */
export function connectionOptionText(row: MCPConnection): { label: string; title: string } {
  return {
    label: `${statusLabel(mcpStatus(row))} · ${row.name}${
      row.description ? ` — ${row.description}` : ''
    }`,
    title: row.description ?? `${row.transport.toUpperCase()} · ${row.endpoint}`,
  };
}

/** Shape of `POST /v1/mcp-connections/:id/test-connection`. */
export interface ITestConnectionResult {
  ok: boolean;
  tools: Array<{
    name: string;
    description?: string;
    inputSchema?: unknown;
    annotations?: {
      readOnlyHint?: boolean;
      destructiveHint?: boolean;
      idempotentHint?: boolean;
      openWorldHint?: boolean;
    };
    /**
     * Formant tool-group taxonomy from the upstream `_meta.group`. Present
     * only for Formant-owned MCPs; third-party servers omit it and fall
     * through to an ungrouped rendering.
     */
    group?: string;
  }>;
  error?: { code: string; message: string };
  cachedAt: string;
}

type IMcpToolDescriptor = ITestConnectionResult['tools'][number];

const UNGROUPED = 'other';

/**
 * Bucket a tool list by `_meta.group`, falling back to the `UNGROUPED` bin
 * for tools without one. Returns an ordered array of `[groupName, tools[]]`
 * pairs so the UI renders groups alphabetically with the `UNGROUPED` bucket
 * always last.
 */
export function groupTools<T extends { group?: string }>(tools: T[]): Array<[string, T[]]> {
  const bins = new Map<string, T[]>();
  for (const t of tools) {
    const key = t.group && t.group.length > 0 ? t.group : UNGROUPED;
    const bucket = bins.get(key);
    if (bucket) bucket.push(t);
    else bins.set(key, [t]);
  }
  return [...bins.entries()].sort(([a], [b]) => {
    if (a === UNGROUPED) return 1;
    if (b === UNGROUPED) return -1;
    return a.localeCompare(b);
  });
}

// ─── Per-tool checkbox UI ───────────────────────────────────────────────────

/**
 * Renders the connection's advertised tools bucketed by `_meta.group`.
 *
 * Grouping is applied only when the upstream tools/list response actually
 * exposes more than one distinct `group` — a connection where nothing carries
 * `_meta.group` (third-party MCP) or where every tool sits in a single group
 * falls through to a flat list. This keeps the UI simple and avoids wrapping
 * a single-bucket surface in a redundant header.
 *
 * When grouping does apply, each group is a collapsible section with a
 * tri-state header checkbox (indeterminate when partially selected) and an
 * explicit ▸ chevron so the affordance reads as expandable without relying on
 * the browser's default `<summary>` marker.
 */
function GroupedToolCheckboxes({
  tools,
  isEnabled,
  pending,
  onToggleTool,
  onToggleGroup,
}: {
  tools: IMcpToolDescriptor[];
  isEnabled: (name: string) => boolean;
  pending: boolean;
  onToggleTool: (name: string) => void;
  onToggleGroup: (names: string[], turnOn: boolean) => void;
}) {
  const groups = useMemo(() => groupTools(tools), [tools]);
  // Flat rendering when the upstream didn't expose more than one unique
  // group — one bucket means nothing to segregate, no header worth showing.
  const isFlat = groups.length <= 1;

  if (isFlat) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {tools.map((t) => (
          <ToolCheckboxRow
            key={t.name}
            tool={t}
            checked={isEnabled(t.name)}
            pending={pending}
            onToggle={() => onToggleTool(t.name)}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      {groups.map(([groupName, toolsInGroup]) => (
        <GroupSection
          key={groupName}
          groupName={groupName}
          toolsInGroup={toolsInGroup}
          isEnabled={isEnabled}
          pending={pending}
          onToggleTool={onToggleTool}
          onToggleGroup={onToggleGroup}
        />
      ))}
    </div>
  );
}

/**
 * One collapsible group. Split out from `GroupedToolCheckboxes` so each row
 * can own its expand/collapse state — needed to drive the explicit chevron
 * without reading from the DOM.
 */
function GroupSection({
  groupName,
  toolsInGroup,
  isEnabled,
  pending,
  onToggleTool,
  onToggleGroup,
}: {
  groupName: string;
  toolsInGroup: IMcpToolDescriptor[];
  isEnabled: (name: string) => boolean;
  pending: boolean;
  onToggleTool: (name: string) => void;
  onToggleGroup: (names: string[], turnOn: boolean) => void;
}) {
  const names = toolsInGroup.map((t) => t.name);
  const enabledCount = names.reduce((n, name) => (isEnabled(name) ? n + 1 : n), 0);
  const allOn = enabledCount === names.length;
  const noneOn = enabledCount === 0;
  // Collapsed by default; auto-open only when the group is restricted so a
  // fully-checked 72-tool surface still renders as ~11 rows. Tracked in React
  // state so the chevron reflects the current open/closed state.
  const [open, setOpen] = useState<boolean>(!allOn);

  return (
    <details open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          listStyle: 'none',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            fontSize: 9,
            width: 10,
            color: 'var(--text-muted)',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 120ms ease',
          }}
        >
          ▶
        </span>
        <input
          type="checkbox"
          className="checkbox"
          checked={allOn}
          disabled={pending}
          ref={(el) => {
            // The indeterminate state can only be set imperatively — HTML
            // treats it as a runtime property, not a React attribute.
            if (el) el.indeterminate = !allOn && !noneOn;
          }}
          onChange={(e) => {
            e.stopPropagation();
            onToggleGroup(names, !allOn);
          }}
          // Prevent the details toggle from firing when the checkbox is
          // clicked directly.
          onClick={(e) => e.stopPropagation()}
          aria-label={`Toggle all ${groupName} tools`}
        />
        <span
          className="t-mono t-xs"
          style={{ fontWeight: 'var(--fw-med)', color: 'var(--text-primary)' }}
        >
          {groupName}
        </span>
        <span className="t-xs t-muted">
          {allOn
            ? `(${names.length} ✓)`
            : noneOn
              ? `(0/${names.length})`
              : `(${enabledCount}/${names.length})`}
        </span>
      </summary>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          // Aligns child rows under the group name (past the chevron +
          // group-checkbox + gaps) so the hierarchy reads visually.
          marginLeft: 'var(--sp-8)',
          marginTop: 4,
        }}
      >
        {toolsInGroup.map((t) => (
          <ToolCheckboxRow
            key={t.name}
            tool={t}
            checked={isEnabled(t.name)}
            pending={pending}
            onToggle={() => onToggleTool(t.name)}
          />
        ))}
      </div>
    </details>
  );
}

function ToolCheckboxRow({
  tool,
  checked,
  pending,
  onToggle,
}: {
  tool: IMcpToolDescriptor;
  checked: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        cursor: pending ? 'default' : 'pointer',
      }}
      title={tool.description ?? tool.name}
    >
      <input
        type="checkbox"
        className="checkbox"
        checked={checked}
        disabled={pending}
        onChange={onToggle}
      />
      <span className="t-mono t-xs">{tool.name}</span>
    </label>
  );
}

// ─── Attached-ref row ───────────────────────────────────────────────────────

/**
 * Per-row body. Owns its own lazy fetch of the MCP's tool list via the same
 * `test-connection` endpoint the Connections page uses — we don't hit the
 * server until the user opens the disclosure.
 */
function AttachedToolRow({
  refName,
  mcp,
  pending,
  onRemove,
  allowlist,
  onAllowlistChange,
}: {
  refName: string;
  mcp: MCPConnection | undefined;
  pending: boolean;
  onRemove: () => void;
  /**
   * The allowlist for THIS connection, or undefined when none is set.
   * `undefined` (or an empty list) means "expose every advertised tool" — the
   * default, no filtering. A non-empty list restricts the consumer to exactly
   * those tool names.
   */
  allowlist: string[] | undefined;
  /** Report a new allowlist for this connection; `undefined` clears it. */
  onAllowlistChange: (next: string[] | undefined) => void;
}) {
  const client = useClient();
  const [open, setOpen] = useState(false);
  const mcpId = mcp?.id;
  const tools = useQuery({
    enabled: open && mcpId !== undefined,
    queryKey: ['mcp-tools', mcpId],
    queryFn: () =>
      client.http.post(`v1/mcp-connections/${mcpId}/test-connection`).json<ITestConnectionResult>(),
  });

  const status = mcp ? mcpStatus(mcp) : null;
  const canProbe = mcp && (mcp.authType === 'none' || mcp.hasKeyring);

  // An empty array behaves identically to "no allowlist" on the backend
  // (both expose all tools), so treat it as unrestricted here too.
  const restrictedTo = Array.isArray(allowlist) && allowlist.length > 0 ? allowlist : null;
  const isRestricted = restrictedTo !== null;
  const advertised = tools.data?.ok ? tools.data.tools.map((t) => t.name) : [];
  const operationTools = tools.data?.ok ? tools.data.tools : [];

  // A tool is enabled if there's no restriction (all on) or it's listed.
  const isEnabled = (name: string): boolean => restrictedTo === null || restrictedTo.includes(name);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
            }}
          >
            <span
              className="t-mono"
              style={{ fontWeight: 'var(--fw-med)', color: 'var(--text-primary)' }}
              title={mcp?.endpoint ?? refName}
            >
              {refName}
            </span>
            {mcp && status ? (
              <>
                <span className="badge badge--tool" title="Resolves to an MCP connection">
                  MCP
                </span>
                <span
                  className="t-xs"
                  style={{
                    color:
                      status === 'ready'
                        ? 'var(--status-success, var(--text-secondary))'
                        : 'var(--status-warning, var(--text-secondary))',
                  }}
                  title={
                    status === 'ready'
                      ? 'Credentials present — this MCP is callable'
                      : 'OAuth keyring missing — connect this MCP from the Connections page before running'
                  }
                >
                  {statusLabel(status)}
                </span>
                {restrictedTo !== null ? (
                  <span
                    className="badge badge--neutral"
                    title={`Restricted to ${restrictedTo.length} tool(s): ${restrictedTo.join(', ')}`}
                  >
                    {restrictedTo.length} of its tools
                  </span>
                ) : (
                  <span className="t-xs t-muted" title="No allowlist set — every tool is available">
                    All tools
                  </span>
                )}
              </>
            ) : (
              <span className="badge badge--neutral" title="Platform tool ID or unresolved ref">
                Platform
              </span>
            )}
          </div>
          {mcp?.description ? (
            <p className="t-xs t-muted" style={{ marginTop: 2 }}>
              {mcp.description}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn--outline btn--sm"
          disabled={pending}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>

      {canProbe ? (
        <details
          onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
          style={{ fontSize: 'var(--fs-xs)' }}
        >
          <summary
            style={{
              cursor: 'pointer',
              color: 'var(--text-muted)',
              userSelect: 'none',
            }}
          >
            {tools.data
              ? restrictedTo !== null
                ? `Tools (${restrictedTo.length} of ${tools.data.tools.length} allowed)`
                : `Tools (${tools.data.tools.length})`
              : open && tools.isLoading
                ? 'Tools (loading…)'
                : 'Tools'}
          </summary>
          <div style={{ marginTop: 'var(--sp-2)' }}>
            {tools.isLoading ? (
              <p className="t-xs t-muted">Loading tools…</p>
            ) : tools.error ? (
              <p className="t-xs" style={{ color: 'var(--status-danger)' }}>
                {(tools.error as Error).message}
              </p>
            ) : tools.data && !tools.data.ok ? (
              <p className="t-xs" style={{ color: 'var(--status-danger)' }}>
                {tools.data.error?.code ?? 'ERROR'}: {tools.data.error?.message ?? 'unknown error'}
              </p>
            ) : tools.data && tools.data.tools.length === 0 ? (
              <p className="t-xs t-muted">No tools advertised by this server.</p>
            ) : tools.data ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                <p className="t-xs t-muted">
                  {isRestricted
                    ? 'Only the checked tools are available. Re-check every tool (or use “Allow all tools”) to remove the restriction. Unchecking every tool also removes the restriction (an empty allowlist means unrestricted) — remove the connection to grant no tools.'
                    : 'All tools are available. Uncheck any to restrict this connection to a subset (e.g. read-only).'}
                </p>
                <GroupedToolCheckboxes
                  tools={operationTools}
                  isEnabled={isEnabled}
                  pending={pending}
                  onToggleTool={(name) =>
                    onAllowlistChange(toggleToolInAllowlist(restrictedTo, advertised, name))
                  }
                  onToggleGroup={(names, turnOn) =>
                    onAllowlistChange(
                      toggleGroupInAllowlist(restrictedTo, advertised, names, turnOn),
                    )
                  }
                />
                {isRestricted ? (
                  <button
                    type="button"
                    className="btn btn--outline btn--sm"
                    style={{ alignSelf: 'flex-start' }}
                    disabled={pending}
                    onClick={() => onAllowlistChange(undefined)}
                  >
                    Allow all tools
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

// ─── The editor ─────────────────────────────────────────────────────────────

export function McpToolsEditor({
  toolRefs,
  toolAllowlists,
  onChange,
  disabled = false,
  showUnknownRefs = true,
}: IMcpToolsEditorProps) {
  const client = useClient();
  const mcpList = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: () => fetchAllMcpConnections(client.http),
  });
  const dedupedConnections = useMemo(
    () => dedupeConnectionsByName(mcpList.data?.items ?? []),
    [mcpList.data],
  );
  const mcpByName = useMemo(() => {
    const map = new Map<string, MCPConnection>();
    for (const row of dedupedConnections) map.set(row.name, row);
    return map;
  }, [dedupedConnections]);

  const value: IMcpToolsValue = { toolRefs, toolAllowlists };
  const connections = dedupedConnections;
  const hasConnections = connections.length > 0;
  // Node-panel mode hides refs that don't resolve to a connection (the panel
  // renders those in its own free-text area) — they're still carried through
  // untouched in every onChange payload.
  const visibleRefs = showUnknownRefs ? toolRefs : toolRefs.filter((r) => mcpByName.has(r));
  const availableMcps = connections.filter((m) => !toolRefs.includes(m.name));

  const [mcpPick, setMcpPick] = useState('');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
        }}
      >
        <p className="t-xs t-muted" style={{ margin: 0 }}>
          MCP connections available at runtime. Adding one exposes every tool that server advertises
          by default — expand a connection to restrict it to a subset of its tools (e.g. read-only).
        </p>
        <Link
          to={routes.MCP_CONNECTIONS}
          className="t-xs"
          style={{ color: 'var(--horizon-glow)', whiteSpace: 'nowrap' }}
        >
          Manage connections
        </Link>
      </div>

      {mcpList.isLoading ? (
        <p className="t-xs t-muted">Loading connections…</p>
      ) : (
        <>
          {/* Attached rows render whenever refs exist — even in an org with
              zero connections (stale names / platform ids still show, so they
              can be inspected and removed). */}
          {visibleRefs.length === 0 ? (
            hasConnections ? (
              <p className="t-sm t-secondary">
                {showUnknownRefs ? 'No tools attached.' : 'No MCP connections attached.'}
              </p>
            ) : null
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {visibleRefs.map((ref, i) => (
                <li
                  key={ref}
                  style={{
                    padding: 'var(--sp-2) 0',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                  }}
                >
                  <AttachedToolRow
                    refName={ref}
                    mcp={mcpByName.get(ref)}
                    pending={disabled}
                    onRemove={() => onChange(removeToolRef(value, ref))}
                    allowlist={toolAllowlists[ref]}
                    onAllowlistChange={(next) => onChange(setConnectionAllowlist(value, ref, next))}
                  />
                </li>
              ))}
            </ul>
          )}

          {!hasConnections ? (
            <p className="t-sm t-secondary">
              No MCP connections in this org yet.{' '}
              <Link
                to={routes.MCP_CONNECTIONS}
                style={{ color: 'var(--horizon-glow)', textDecoration: 'underline' }}
              >
                Manage connections
              </Link>{' '}
              to add one.
            </p>
          ) : (
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--sp-3)' }}>
              <span className="field-label">Add MCP connection</span>
              <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-1)' }}>
                <select
                  className="select-native"
                  style={{ flex: 1 }}
                  value={mcpPick}
                  onChange={(e) => setMcpPick(e.target.value)}
                  disabled={disabled || availableMcps.length === 0}
                >
                  <option value="">
                    {availableMcps.length === 0
                      ? 'No more MCP connections to add'
                      : '— Select a connection —'}
                  </option>
                  {availableMcps.map((m) => {
                    const opt = connectionOptionText(m);
                    return (
                      <option key={m.id} value={m.name} title={opt.title}>
                        {opt.label}
                      </option>
                    );
                  })}
                </select>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!mcpPick || disabled}
                  onClick={() => {
                    onChange(addConnectionRef(value, mcpPick));
                    setMcpPick('');
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
